import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Modal } from 'reactstrap';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import html2canvas from 'html2canvas';

// Hooks
import { useEventTools } from '../../hooks/useEventTools';

// constants
import appIcons from '../../constants/appIcons';
import LocalStrings from '../../assets/languages';
import { ALLOWED_FEATURE_KEYS } from '../../constants/section-constants/common-constants';
import {
  ANALYTICS_URL,
  CDN_URL,
  INITIAL_APP_THEME
} from '../../constants/constant';
import { ALLOWED_POST_ANALYTICS_TYPES } from '../../constants/section-constants/type-constants';
import {
  getAppTabId,
  getCompanyDesignation,
  getFullName,
  getQRUrl,
  getSameDomainBaseUrl,
  getThumbImageUrl,
  getUserRolePrivilege,
  renderProfileSrc,
  replaceAll
} from '../../util/util-common';

// action
import {
  clearExhibitorDetails,
  fetchAllExhibitor,
  fetchExhibitorAnalytics,
  setExhibitor
} from '../exhibitors/actions';
import {
  fullScreenLoaderHandler,
  postAnalytics,
  redirectingCalledFrom,
  setProfilePortalToggle
} from '../../actions';
import {
  clearSponsorDetails,
  fetchAllSponsor,
  setSponsor
} from '../sponsors/actions';
import {
  fetchGameChallengeStatus,
  setGamificationPopup
} from '../gamification/actions';
import { fetchAllSpeakers, openSpeakerDetails } from '../speakers/actions';
import { fetchTrackSessions } from '../sessions/actions';
import { getUserQR } from '../myscans/actions';

// components
import { showToast } from '../Common/alertmessage';
import TextWithLink from '../Common/TextWithLink';

// file
import NoImgIcon from '../../assets/img/placeholders/NoImg.svg';
import fbIcon from '../../assets/img/contact-icons/facebook.png';
import twitterIcon from '../../assets/img/contact-icons/twitter.png';
import linkedinIcon from '../../assets/img/contact-icons/linkedin.png';
import instagramIcon from '../../assets/img/contact-icons/instagram.png';

const EventTools = props => {
  const {
    session,
    eventDetails,
    eventSettings,
    scheduleAppTabId,
    speakerDetails,
    profileToggleReducer,
    rolePermissions,
    showAccessDenied
  } = props;

  const history = useHistory();
  const qrRef = useRef(null);
  const eventTools = useEventTools();

  const [allQRData, setAllQRData] = useState(null);
  const [exhibitorAnalysisData, setExhibitorAnalysisData] = useState(null);
  const [showToolDetailsModal, setShowToolDetailsModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);

  useEffect(() => {
    props.getUserQR(session, res => {
      if (res?.success) setAllQRData(res?.data);
    });
  }, []);

  const socialList = useMemo(() => {
    if (selectedTool?.type === ALLOWED_FEATURE_KEYS.speaker) {
      const details = selectedTool?.details;

      return [
        ...(!_.isEmpty(details?.facebook)
          ? [
              {
                icon: fbIcon,
                link: details?.facebook
              }
            ]
          : []),
        ...(!_.isEmpty(details?.twitter)
          ? [
              {
                icon: twitterIcon,
                link: details?.twitter
              }
            ]
          : []),
        ...(!_.isEmpty(details?.linkedin)
          ? [
              {
                icon: linkedinIcon,
                link: details?.linkedin
              }
            ]
          : []),
        ...(!_.isEmpty(details?.instagram)
          ? [
              {
                icon: instagramIcon,
                link: details?.instagram
              }
            ]
          : [])
      ];
    }
    return [];
  }, [selectedTool]);

  const handleCloseModal = () => {
    setShowToolDetailsModal(false);
    props.openSpeakerDetails(null, { show: false, value: {} });
  };

  const handleDashboardSelect = tool => {
    setSelectedTool(tool);
    setShowToolDetailsModal(true);

    switch (tool?.type) {
      case ALLOWED_FEATURE_KEYS.speaker:
        props.openSpeakerDetails(
          session,
          {
            show: true,
            value: tool?.details,
            calledFrom: 'event-tools'
          },
          { scheduleApptabId: scheduleAppTabId }
        );
        break;

      case ALLOWED_FEATURE_KEYS.exhibitor:
      case ALLOWED_FEATURE_KEYS.sponsor:
        props.fetchExhibitorAnalytics(
          session,
          {
            type: tool?.type,
            id: tool?.id
          },
          res => {
            if (res.success && res.message == 'Success') {
              setExhibitorAnalysisData(res.data);
            }
          }
        );
        break;

      default:
        break;
    }
  };

  const handleRedirection = type => {
    switch (type) {
      case ALLOWED_FEATURE_KEYS.exhibitor:
        props.redirectingCalledFrom('event-tool');
        // --- clear
        props.setExhibitor({});
        props.clearExhibitorDetails();
        // --- redirection
        history.push('/exhibitors/' + selectedTool?.tabid);
        // --- fetch
        props.fetchAllExhibitor(
          session,
          {
            type: 'redirection',
            id: selectedTool?.id,
            sort: selectedTool?.sorttype,
            eventDetails
          },
          res => {}
        );
        setShowToolDetailsModal(false);
        break;

      case ALLOWED_FEATURE_KEYS.sponsor:
        props.redirectingCalledFrom('event-tool');
        // --- clear
        props.setSponsor({});
        props.clearSponsorDetails();
        // --- redirection
        history.push('/sponsors/' + selectedTool?.tabid);
        // --- fetch
        props.fetchAllSponsor(
          session,
          {
            type: 'redirection',
            id: selectedTool?.id,
            sort: selectedTool?.sorttype,
            eventDetails
          },
          res => {}
        );
        setShowToolDetailsModal(false);

        // Post Analytics Data
        props.postAnalytics(
          session,
          eventDetails,
          [
            ALLOWED_POST_ANALYTICS_TYPES.screen.sponsor,
            ALLOWED_POST_ANALYTICS_TYPES.type.entry,
            selectedTool?.id,
            null,
            0
          ],
          res => {}
        );

        // Gamification Status
        props.fetchGameChallengeStatus(
          session,
          ALLOWED_GAME_CHALLENGE_TYPES.types.sponsor,
          response => {
            if (response.data && response.data.iscompleted) {
              if (response.data.showpopup) {
                response.data.challengetype =
                  ALLOWED_GAME_CHALLENGE_TYPES.types.sponsor;
                props.setGamificationPopup(true, response.data);
              } else if (response.data?.showpopup) {
                showToast(
                  `${LocalStrings.dynamic.kYouHaveGot} ${response.data.points} ${LocalStrings.dynamic.kPointsText}`
                );
              }
            }
          }
        );

        break;

      case ALLOWED_FEATURE_KEYS.speaker:
        props.redirectingCalledFrom('event-tool');
        // --- clear
        props.openSpeakerDetails(null, {});
        // --- redirection
        history.push('/speakers/' + selectedTool?.tabid);
        // --- Fetch
        props.fetchAllSpeakers(
          session,
          {
            type: 'redirection',
            id: selectedTool?.id,
            sort: selectedTool?.sorttype,
            featured:
              eventSettings?.overview_settings?.viewtype_speaker || false
          },
          res => {}
        );
        setShowToolDetailsModal(false);
        // Post Analytics Data
        props.postAnalytics(
          session,
          eventDetails,
          [
            ALLOWED_POST_ANALYTICS_TYPES.screen.speakers,
            ALLOWED_POST_ANALYTICS_TYPES.type.entry,
            selectedTool?.id,
            null,
            0
          ],
          res => {}
        );

        // Gamification Status
        props.fetchGameChallengeStatus(
          session,
          ALLOWED_GAME_CHALLENGE_TYPES.types.speakers,
          response => {
            if (response.data && response.data.iscompleted) {
              if (response.data.showpopup) {
                response.data.challengetype =
                  ALLOWED_GAME_CHALLENGE_TYPES.types.speakers;
                props.setGamificationPopup(true, response.data);
              } else if (response.data?.showpopup) {
                showToast(
                  `${LocalStrings.dynamic.kYouHaveGot} ${response.data.points} ${LocalStrings.dynamic.kPointsText}`
                );
              }
            }
          }
        );

        break;

      default:
        break;
    }
  };

  const handleDownloadImage = async () => {
    const element = qrRef.current;
    await html2canvas(element, {
      allowTaint: true,
      useCORS: true
    }).then(canvas => {
      let data = canvas.toDataURL('image/jpg');
      let link = document.createElement('a');
      link.href = data;
      link.download = `${replaceAll(
        `${selectedTool?.typeText} ${LocalStrings.dynamic.kQRCodeText}`,
        ' ',
        '_'
      )}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const handleEditLinkedProfile = () => {
    history.push({
      pathname: _.isEqual(selectedTool?.type, ALLOWED_FEATURE_KEYS.exhibitor)
        ? '/myprofile-exhibitor'
        : _.isEqual(selectedTool?.type, ALLOWED_FEATURE_KEYS.sponsor)
        ? '/myprofile-sponsor'
        : _.isEqual(selectedTool?.type, ALLOWED_FEATURE_KEYS.speaker)
        ? '/myprofile-speaker'
        : '/myprofile'
    });
  };

  const redirectToAnalytics = () => {
    window.open(
      replaceAll(
        ANALYTICS_URL,
        ['###BASE_URL###', '##TYPEID##', '##TYPE##', '##SESSION_TOKEN##'],
        [
          getSameDomainBaseUrl(session),
          String(selectedTool?.id),
          selectedTool?.type,
          session?.sessionToken
        ]
      ),
      '_blank'
    );
  };

  const _renderDetailsModal = useCallback(() => {
    const details = selectedTool?.details;

    return (
      <Modal isOpen={showToolDetailsModal} size="md" centered>
        <div
          className={
            _.isEqual(session.theme, INITIAL_APP_THEME)
              ? 'bg-offWhite'
              : 'bg-white'
          }
          style={{
            paddingBottom: '15px'
          }}
        >
          <div className="modal-header d-flex justify-content-between align-items-center">
            <h5 className="modal-title custom-text2-medium-primary">
              <i className="fa-regular fa-arrow-down-to-bracket"></i>
              {selectedTool?.display}
            </h5>

            {/*  - QR Code - Edit profile - Close Button - */}
            <div className="d-flex align-items-center gap-2 ml-auto cursor-pointer">
              {/* QR code */}
              {rolePermissions?.qr && (
                <button
                  aria-label="Close"
                  className="close"
                  data-dismiss="modal"
                  type="button"
                  onClick={() => setShowQRModal(true)}
                >
                  <span className="font-icomoon custom-text3-medium-primary">
                    {appIcons.qrCodeIcon}
                  </span>
                </button>
              )}

              {/* Edit profile */}
              <button
                aria-label="Close"
                className="close"
                data-dismiss="modal"
                type="button"
                onClick={handleEditLinkedProfile}
              >
                <span className="font-icomoon custom-text3-medium-primary">
                  {appIcons.notesIcon}
                </span>
              </button>

              {/* Close */}
              <button
                aria-label="Close"
                className="close"
                data-dismiss="modal"
                type="button"
                onClick={handleCloseModal}
              >
                <span className="font-icomoon custom-text4-medium-primary">
                  {appIcons.crossIcon}
                </span>
              </button>
            </div>
          </div>

          {/* Scrollable Modal Body */}
          <div
            className="scrollbar-hide-no-maxheight"
            style={{ maxHeight: '550px' }}
          >
            {selectedTool?.type === ALLOWED_FEATURE_KEYS.speaker &&
            speakerDetails?.show &&
            !showAccessDenied ? (
              <div className="col-12 p-3  align-items-center">
                {/* Right Section - View Profile Button */}
                <div
                  className="d-flex justify-content-end position-relative"
                  style={{ right: '15px', top: '15px' }}
                >
                  <button
                    className="custom-button-default-md"
                    style={{
                      color: 'var(--theme-color)',
                      marginLeft: '1rem'
                    }}
                    onClick={() => handleRedirection(selectedTool?.type)}
                  >
                    <span>{LocalStrings.dynamic.kViewProfile}</span>
                  </button>
                </div>

                {/* Left Section - Profile Image */}
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ flex: '1' }}
                >
                  <LazyLoadImage
                    style={{
                      height: '250px', // Ensuring 3:2 aspect ratio
                      objectFit: 'contain',
                      aspectRatio: '2/3',
                      borderRadius: '10px'
                    }}
                    alt="Profile Image"
                    effect="blur"
                    placeholder={
                      <img
                        src={NoImgIcon}
                        style={{ width: '150px', height: '100px' }}
                        alt="lazy load placeholder on error"
                      />
                    }
                    src={renderProfileSrc(details, selectedTool?.type)}
                    onError={e => {
                      e.target.onerror = null;
                      e.target.src = NoImgIcon;
                    }}
                  />
                </div>

                {/* Center Section - User Details */}
                <div
                  className="d-flex flex-column text-center mx-3"
                  style={{ flex: '2' }}
                >
                  <div className="custom-text2-medium-primary">
                    {getFullName(details)}
                  </div>
                  <div className="custom-text1-normal">
                    {getCompanyDesignation(details)}
                  </div>
                  {!_.isEmpty(details?.country) && (
                    <div className="custom-text1-normal">
                      <span>{LocalStrings.dynamic.kCountryText} : </span>
                      <span>{details?.country}</span>
                    </div>
                  )}
                </div>

                {/* Social Icons */}
                <div className="d-flex justify-content-center mt-3">
                  {!_.isEmpty(socialList) &&
                    _.map(socialList, (ele, index) => (
                      <a
                        key={`event_tools_speaker_social_item_${index}`}
                        href={ele?.link}
                        className="mx-2"
                      >
                        <img
                          onError={e => {
                            e.target.onerror = null;
                            e.target.src = NoImgIcon;
                          }}
                          style={{ height: '2.25rem' }}
                          src={ele?.icon}
                        />
                      </a>
                    ))}
                </div>

                {/* Description Section */}
                {!_.isEmpty(details?.description) && (
                  <div className="container-user-details mt-4">
                    <div className="custom-text2-normal-primary mb-1">
                      {LocalStrings.dynamic.kAbout}
                    </div>
                    <div className="custom-text1-normal whiteBgBorder10">
                      <TextWithLink
                        text={details?.description}
                        maxlines={3}
                        expand
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div
                className={`exhibitor_details_main_container p-3 ${
                  _.isEqual(session.theme, INITIAL_APP_THEME)
                    ? 'bg-offWhite'
                    : 'bg-white'
                }`}
              >
                <div className="">
                  {/* ----- Banner title booth section ----- */}
                  <div
                    className={`exhibitor_details_main_container ${
                      _.isEqual(session.theme, INITIAL_APP_THEME)
                        ? 'bg-white'
                        : 'bg-offWhite'
                    }`}
                    style={{
                      padding: '0.75rem',
                      borderRadius: '0.625rem'
                    }}
                  >
                    {/* ------- Banner Section -------- */}
                    {!_.isEmpty(details?.bannerimage) && (
                      <div className="position-relative">
                        <LazyLoadImage
                          className="img-fluid w-100"
                          style={{ borderRadius: '5px' }}
                          alt="Image"
                          effect="blur"
                          placeholder={
                            <img
                              src={NoImgIcon}
                              alt="lazy load placeholder on error"
                              style={{
                                height: '18  s   0px',
                                objectFit: 'cover'
                              }}
                            />
                          }
                          src={
                            details?.bannerimage
                              ? `${CDN_URL}${getThumbImageUrl(
                                  details?.bannerimage,
                                  null
                                )}`
                              : ''
                          }
                          onError={e => {
                            e.target.onerror = null;
                            e.target.src = NoImgIcon;
                          }}
                        />
                      </div>
                    )}

                    {/* Small Logo Positioned Bottom-Left */}
                    <div className="w-100 h-100" style={{ zIndex: 2 }}>
                      <LazyLoadImage
                        className={`${
                          !_.isEmpty(details?.bannerimage)
                            ? 'position-absolute'
                            : ''
                        }`}
                        style={{
                          bottom: '-52px',
                          left: '12px',
                          width: '75px',
                          height: '75px',
                          borderRadius: '10px',
                          border: '2px solid white'
                        }}
                        alt="Image"
                        effect="blur"
                        placeholder={
                          <img
                            src={NoImgIcon}
                            style={{ width: '77pt', height: '77pt' }}
                            alt="lazy load placeholder on error"
                          />
                        }
                        src={renderProfileSrc(details, selectedTool?.type)}
                        onError={e => {
                          e.target.onerror = null;
                          e.target.src = NoImgIcon;
                        }}
                      />
                    </div>

                    {/* -------- Name Section -------- */}
                    <div className="" style={{ margin: '5px 110px' }}>
                      <h5 className="fw-bold">{details?.name}</h5>
                      {/* {details?.booth && ( */}
                      <div>
                        <span className="custom-button-primary-sm">
                          {LocalStrings.dynamic.kBoothText} {details?.booth}
                        </span>
                      </div>
                      {/* )} */}
                    </div>

                    {/* Edit Exhibitor Profile Button */}
                    <div className="text-center mt-6">
                      <button
                        className="custom-button-default-md w-100"
                        style={{
                          color: 'var(--theme-color)',
                          marginTop: '1rem'
                        }}
                        onClick={() => handleRedirection(selectedTool?.type)}
                      >
                        <span>{LocalStrings?.dynamic?.kViewProfile}</span>
                      </button>
                    </div>
                  </div>

                  {/* -------- Stats Section -------- */}
                  <div
                    className="exhibitor_details_stats_container"
                    style={{ marginTop: '22px' }}
                  >
                    <div className="exb_stats_label">
                      <h5 className="custom-text6-medium-primary pull-left">
                        {`${selectedTool?.typeText} ${LocalStrings.dynamic.kStatsText}`}
                      </h5>
                    </div>
                  </div>

                  {/* Exhibitor Stats */}
                  <div
                    className="exhibitor_details_stats_container"
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'space-between',
                      marginTop: '0rem'
                    }}
                  >
                    <div className="exb_stats_column" style={{ width: '48%' }}>
                      {/* - Views - */}
                      <div
                        className="exb_stats_details_container whiteBgBorder10"
                        style={{ marginBottom: '12px' }}
                      >
                        <div className="stat_items">
                          <div
                            style={{ textAlign: 'initial' }}
                            className="exhibitor_details_individual_stats custom-text1-normal-secondary"
                          >
                            <span>
                              <span className="stat_icon">
                                {appIcons?.viewEyeIcon}
                              </span>{' '}
                              {exhibitorAnalysisData?.views}
                            </span>

                            <span>
                              {exhibitorAnalysisData?.views > 1
                                ? LocalStrings.dynamic.kViewsText
                                : LocalStrings.dynamic.kViewText}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* - Bookmarked - */}
                      <div
                        className="exb_stats_details_container whiteBgBorder10"
                        style={{ marginBottom: '12px' }}
                      >
                        <div className="stat_items">
                          <div
                            style={{ textAlign: 'initial' }}
                            className="exhibitor_details_individual_stats custom-text1-normal-secondary"
                          >
                            <span>
                              <span className="stat_icon">
                                {appIcons.bookmarksIcon}
                              </span>{' '}
                              {exhibitorAnalysisData?.bookmarked}
                            </span>

                            <span>{LocalStrings.dynamic.kBookmarkedText}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="exb_stats_column" style={{ width: '48%' }}>
                      {/* - Message - */}
                      <div
                        className="exb_stats_details_container whiteBgBorder10"
                        style={{ marginBottom: '12px' }}
                      >
                        <div className="stat_items">
                          <div
                            style={{ textAlign: 'initial' }}
                            className="exhibitor_details_individual_stats custom-text1-normal-secondary"
                          >
                            <span>
                              <span className="stat_icon">
                                {appIcons.messageIcon}
                              </span>{' '}
                              {exhibitorAnalysisData?.messages}
                            </span>

                            <span>
                              {exhibitorAnalysisData?.messages > 1
                                ? LocalStrings.dynamic.kMessagesText
                                : LocalStrings.dynamic.kMessage}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* - Email Clicked - */}
                      <div
                        className="exb_stats_details_container whiteBgBorder10"
                        style={{ marginBottom: '12px' }}
                      >
                        <div className="stat_items">
                          <div
                            style={{ textAlign: 'initial' }}
                            className="exhibitor_details_individual_stats custom-text1-normal-secondary"
                          >
                            <span>
                              <span className="stat_icon">
                                {appIcons.envelopIcon}
                              </span>{' '}
                              {exhibitorAnalysisData?.email}
                            </span>
                            <span>{LocalStrings.dynamic.kEmailClick}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* View More Button */}
                  <div className="text-center mt-6">
                    <button
                      className="custom-button-default-md w-100 bg-white"
                      style={{ color: 'var(--theme-color)', marginTop: '1rem' }}
                      onClick={redirectToAnalytics}
                    >
                      <span>{LocalStrings.dynamic.kViewMoreText}</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    );
  }, [selectedTool, showToolDetailsModal, session, exhibitorAnalysisData]);

  const _renderQRdetails = () => {
    if (_.isEmpty(selectedTool) || _.isEmpty(allQRData)) return <></>;

    const qrUrl = getQRUrl(session, allQRData[selectedTool?.type]?.qr);
    const detailsName = _.isEqual(
      selectedTool?.type,
      ALLOWED_FEATURE_KEYS.speaker
    )
      ? getFullName(selectedTool?.details)
      : selectedTool?.details?.name;

    return (
      <Modal
        className={'checkoutform-upload-modal'}
        isOpen={showQRModal}
        centered
      >
        <div className="modal-body" style={{ padding: '3rem 2rem' }}>
          {/* ----- Modal header ----- */}
          <div className="row pb-3 heading-section border-bottom">
            <div className="col-10">
              <h5
                className="modal-title custom-text1-bold"
                id="modal-title-default"
              >
                {LocalStrings.dynamic.kMyQR}
              </h5>
            </div>

            <div className="col-2">
              <button
                aria-label="Close"
                className="close"
                data-dismiss="modal"
                type="button"
                onClick={() => setShowQRModal(false)}
              >
                <span className="font-icomoon custom-text4-medium-primary">
                  {appIcons.crossIcon}
                </span>
              </button>
            </div>
          </div>

          {/* ----- Modal body ----- */}
          <div className="d-flex h-100 flex-column justify-content-between align-items-center pt-3 px-3">
            <div
              style={{ flexGrow: 1, padding: '20px', borderRadius: '20px' }}
              ref={qrRef}
              className="text-center d-flex flex-column justify-content-center align-items-center "
            >
              <div className="modal-title custom-text1-bold pb-3 pt-2">
                {selectedTool?.typeText} {LocalStrings.dynamic.kQRCodeText}
              </div>
              <img
                className="user-qr-img"
                alt="Image"
                src={qrUrl}
                style={{ width: '260px' }}
              />

              <div style={{ height: '100px' }}>
                <div className="pt-3">
                  <div className={`custom-text1-medium`}>{detailsName}</div>
                  <div className="custom-button-outerline-primary-sm-tags h-auto">
                    {selectedTool?.typeText}
                  </div>
                </div>
              </div>
            </div>
            <div
              className="custom-text9-normal-theme d-flex align-items-center justify-content-between"
              style={{ gap: '5px' }}
            >
              <span className="menu-icons cursor-pointer m-0">
                {appIcons.eventifyExploreIcon}
              </span>
              {_.isEqual(selectedTool?.type, ALLOWED_FEATURE_KEYS.exhibitor)
                ? LocalStrings.dynamic.kScanQRExhibitor
                : _.isEqual(selectedTool?.type, ALLOWED_FEATURE_KEYS.speaker)
                ? LocalStrings.dynamic.kScanQRSpeaker
                : LocalStrings.dynamic.kScanQRSponsor}

              <span
                className="menu-icons m-0 cursor-pointer"
                onClick={handleDownloadImage}
                style={{ color: 'gray' }}
              >
                {appIcons.downloadIcon}
              </span>
            </div>
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <>
      {/* ===== Side bar component ===== */}
      {!_.isEmpty(eventTools?.dashboardTypes) && (
        <>
          {/* ----- Tools section ----- */}
          <div className="border-bottom pb-2">
            {/* --- Section heading --- */}
            <div className="custom-text8-medium-secondary">
              <span>{LocalStrings.dynamic.kEventTools}</span>
            </div>

            {/* --- Available tools listing --- */}
            {_.map(eventTools?.dashboardTypes, type => {
              return (
                <div
                  key={`event_tool_${type?.type}_${type?.tabid}`}
                  className="d-flex justify-content-between align-items-center px-1 py-2 unselected-menu menu-name"
                  onClick={() => {
                    handleDashboardSelect(type);
                  }}
                >
                  <span className="">{type?.display}</span>
                  <i
                    className="fa-regular fa-arrow-right custom-text8-medium-secondary"
                    style={{ rotate: '-50deg' }}
                  ></i>
                </div>
              );
            })}

            {/* --- Lead retrieval button --- */}
            {!profileToggleReducer?.show && rolePermissions?.show_retrieve ? (
              <div
                className="d-flex justify-content-between align-items-center px-1 py-2 unselected-menu menu-name"
                onClick={() =>
                  history.push(
                    location.pathname === '/myscans' ? '/overview' : '/myscans'
                  )
                }
              >
                <span className="">
                  {location.pathname === '/myscans'
                    ? LocalStrings.dynamic.kEXIT
                    : ''}
                  {LocalStrings?.dynamic.KLeadRetrival}
                </span>
                <i
                  className="fa-regular fa-arrow-right"
                  style={{ rotate: '-50deg' }}
                ></i>
              </div>
            ) : (
              ''
            )}
          </div>

          {/* ----- Menu section heading ----- */}
          <span className="custom-text8-medium-secondary my-2">
            {LocalStrings.dynamic.kEventMenu}
          </span>
        </>
      )}

      {/* ===== Tool details modal ===== */}
      {_renderDetailsModal()}

      {/* ===== QR Modal ===== */}
      {_renderQRdetails()}
    </>
  );
};

export const mapStateToProps = state => {
  const rolePermissions = getUserRolePrivilege(
    {
      ...state.userDetails,
      ...state.userDetailsAddOn
    },
    state.eventTabs,
    state.userAccessByRole,
    ALLOWED_FEATURE_KEYS.scans
  );

  const scheduleAppTabId = getAppTabId(
    state.eventTabs,
    ALLOWED_FEATURE_KEYS.schedule
  );

  return {
    session: state.session,
    eventDetails: state.eventDetails,
    eventSettings: state.eventSettings,
    speakerDetails: state.speakerDetailsReducer,
    showAccessDenied: state.commonAccessDeniedRender,
    profileToggleReducer: state.profileToggleReducer,
    scheduleAppTabId,
    rolePermissions
  };
};

export const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      fetchExhibitorAnalytics,
      redirectingCalledFrom,
      setSponsor,
      clearExhibitorDetails,
      setExhibitor,
      clearSponsorDetails,
      fetchAllSponsor,
      fetchGameChallengeStatus,
      openSpeakerDetails,
      setGamificationPopup,
      fetchAllSpeakers,
      fetchAllExhibitor,
      postAnalytics,
      fetchTrackSessions,
      fullScreenLoaderHandler,
      getUserQR,
      setProfilePortalToggle
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(EventTools);
